import React, { useState, useEffect, useCallback } from 'react';
import { BottomSheet, ToggleSwitch } from '../../../shared/ui';
import { questionsV2Api } from '../../../api/questionsV2Api';
import './ChatMemberPermissionsModal.css';

/** Коды и подписи прав для участника чата по typeId (1=вопросы, 2=заметки, 3=снабжение) */
const CHAT_PERMISSION_SPEC: Record<number, { code: string; label: string }[]> = {
  1: [
    { code: 'QUESTION_CHAT_READ', label: 'Читать чат' },
    { code: 'QUESTION_CHAT_WRITE', label: 'Писать в чат' },
    { code: 'QUESTION_ADD_MEMBERS', label: 'Добавлять участников' },
    { code: 'QUESTION_EDIT', label: 'Редактировать' },
    { code: 'QUESTION_DELETE', label: 'Удалять' },
    { code: 'QUESTION_CLOSE', label: 'Закрывать' },
  ],
  2: [
    { code: 'NOTE_CHAT_READ', label: 'Читать чат' },
    { code: 'NOTE_CHAT_WRITE', label: 'Писать в чат' },
    { code: 'NOTE_ADD_MEMBERS', label: 'Добавлять участников' },
    { code: 'NOTE_EDIT', label: 'Редактировать' },
    { code: 'NOTE_DELETE', label: 'Удалять' },
    { code: 'NOTE_CLOSE', label: 'Закрывать' },
  ],
  3: [
    { code: 'SUPPLY_CHAT_READ', label: 'Читать чат' },
    { code: 'SUPPLY_CHAT_WRITE', label: 'Писать в чат' },
    { code: 'SUPPLY_ADD_MEMBERS', label: 'Добавлять участников' },
    { code: 'SUPPLY_EDIT', label: 'Редактировать' },
    { code: 'SUPPLY_DELETE', label: 'Удалять' },
    { code: 'SUPPLY_CLOSE', label: 'Закрывать' },
  ],
};

const DEFAULT_SPEC = CHAT_PERMISSION_SPEC[1];

interface ChatMemberPermissionsModalProps {
  isOpen: boolean;
  siteId: number;
  questionId: number;
  userId: number;
  typeId: number;
  userName: string;
  onClose: () => void;
  onSaved?: () => void;
}

const ChatMemberPermissionsModal: React.FC<ChatMemberPermissionsModalProps> = ({
  isOpen,
  siteId,
  questionId,
  userId,
  typeId,
  userName,
  onClose,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<Set<string>>(new Set());

  const spec = CHAT_PERMISSION_SPEC[typeId] ?? DEFAULT_SPEC;

  const load = useCallback(async () => {
    if (!siteId || !questionId || !userId) return;
    try {
      setLoading(true);
      setError(null);
      const list = await questionsV2Api.getQuestionMemberPermissions(siteId, questionId, userId);
      setCodes(new Set(list));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Ошибка загрузки прав';
      setError(msg);
      setCodes(new Set());
    } finally {
      setLoading(false);
    }
  }, [siteId, questionId, userId]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const toggle = (code: string) => {
    setCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await questionsV2Api.updateQuestionMemberPermissions(siteId, questionId, userId, Array.from(codes));
      onSaved?.();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Ошибка сохранения прав';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <BottomSheet
      isOpen={isOpen}
      title="Права участника в чате"
      onClose={onClose}
      size="auto"
    >
      <div className="chat-member-permissions-modal">
        <div className="chat-member-permissions-user">
          <i className="ri-user-line"></i>
          <span>{userName || 'Участник'}</span>
        </div>
        {error && (
          <div className="chat-member-permissions-error">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}
        {loading ? (
          <div className="chat-member-permissions-loading">
            <i className="ri-loader-4-line ri-spin"></i>
            <span>Загрузка...</span>
          </div>
        ) : (
          <div className="chat-member-permissions-list">
            {spec.map(({ code, label }) => (
              <ToggleSwitch
                key={code}
                label={label}
                checked={codes.has(code)}
                onChange={() => toggle(code)}
                disabled={saving}
              />
            ))}
          </div>
        )}
        <div className="chat-member-permissions-actions">
          <button type="button" className="chat-member-permissions-btn secondary" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="chat-member-permissions-btn primary"
            onClick={handleSave}
            disabled={loading || saving}
          >
            {saving ? <i className="ri-loader-4-line ri-spin"></i> : <i className="ri-check-line"></i>}
            Сохранить
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default ChatMemberPermissionsModal;
